import { ReactElement, useRef, useState } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { heapEvent } from '@equipmentshare/heaper'
import { useAssignPickOrderMutation, usePickOrderAssigneesQuery } from '../../../../graphql'
import { useShowError, useShowSuccess } from '../../../../hooks/useToast'
import { MIN_ASSIGNEE_SEARCH_LENGTH } from './CreatePickOrderDialog'
import { AssigneeOption, assigneeOptionLabel, toAssigneeOptions } from '../assigneeOptions'
import { userDisplayName } from '../userDisplayName'

type Props = {
	pickOrderId: string,
	assignedToUser?: { id: string, firstName?: string | null, lastName?: string | null } | null,
	disabled?: boolean,
}

/**
 * Who is picking this run, and the way to change it.
 *
 * The name is the control. Reassigning is rare beside the picking itself, so it
 * earns no standing surface of its own: it sits inline where the assignee is
 * already named and opens over the page rather than pushing the work down.
 *
 * Choosing somebody assigns immediately. The choice is the instruction, and
 * reassigning is its own undo, so a confirming tap would only add a way to miss.
 */
export default function AssignPickOrderControl( {
	pickOrderId,
	assignedToUser,
	disabled,
}: Props ): ReactElement {
	const showSuccess = useShowSuccess()
	const showError = useShowError()
	const anchorRef = useRef<HTMLButtonElement>( null )
	const [ open, setOpen ] = useState( false )
	const [ search, setSearch ] = useState( '' )
	const [ listOpen, setListOpen ] = useState( false )
	const isSearchable = search.trim().length >= MIN_ASSIGNEE_SEARCH_LENGTH

	const { data, loading } = usePickOrderAssigneesQuery( {
		variables: { search },
		skip: !open || !isSearchable,
	} )

	const options = toAssigneeOptions( data?.users.edges.map( ( edge ) => edge.node ) ?? [] )
	const [ assignPickOrder, { loading: isAssigning } ] = useAssignPickOrderMutation()

	const currentName = userDisplayName( assignedToUser )

	const close = () => {
		setOpen( false )
		setListOpen( false )
		setSearch( '' )
	}

	const assignTo = async( option: AssigneeOption ) => {
		try {
			const result = await assignPickOrder( {
				variables: { input: { pickOrderId, userId: option.id } },
			} )
			const assigned = result.data?.assignPickOrder
			if ( !assigned?.success ) {
				showError( `assignPickOrder-${pickOrderId}`, assigned?.message ?? 'Could not assign the pick order.' )
				return
			}
			heapEvent( 'Inventory - DC Admin - Pick Order - Assign - Success', { pick_order_id: pickOrderId } )
			showSuccess( `assignPickOrder-${pickOrderId}`, `Assigned to ${option.name}.` )
		} catch ( err ) {
			showError( `assignPickOrder-${pickOrderId}`, err instanceof Error ? err.message : 'Could not assign the pick order.' )
		}
	}

	// Deliberately not async: the outcome is a toast, so nothing here waits on it,
	// and an async handler would make Autocomplete's onChange return a promise.
	const handleSelect = ( option: AssigneeOption | null ) => {
		if ( !option ) {
			return
		}
		close()
		assignTo( option )
	}

	return (
		<>
			<Button
				ref={anchorRef}
				variant="text"
				color="inherit"
				size="small"
				disabled={isAssigning || disabled === true}
				onClick={() => setOpen( true )}
				endIcon={isAssigning
					? <CircularProgress size={14} color="inherit" />
					: <ArrowDropDownIcon fontSize="small" />}
				// It sits in a line of body text, so it carries that line's weight
				// rather than a button's — while still clearing the touch floor.
				sx={{
					minHeight: ( theme ) => theme.touch.minTarget,
					px: 1,
					mx: -1,
					font: 'inherit',
					textTransform: 'none',
					color: currentName ? 'text.secondary' : 'primary.main',
				}}
			>
				{currentName ?? 'Assign someone'}
			</Button>

			<Popover
				open={open}
				anchorEl={anchorRef.current}
				onClose={close}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				slotProps={{ paper: { sx: { width: 340, maxWidth: '100%', p: 2 } } }}
			>
				<Autocomplete<AssigneeOption>
					options={options}
					loading={loading}
					// The list stays shut until there is a search to show results for; the
					// placeholder already says what to do.
					open={listOpen && isSearchable}
					onOpen={() => setListOpen( true )}
					onClose={() => setListOpen( false )}
					onChange={( _event, option ) => handleSelect( option )}
					inputValue={search}
					onInputChange={( _event, value ) => setSearch( value )}
					getOptionLabel={assigneeOptionLabel}
					isOptionEqualToValue={( option, value ) => option.id === value.id}
					// The search already ran server-side; re-filtering here would only
					// hide people the query deliberately matched on email or employee id.
					filterOptions={( all ) => all}
					noOptionsText="No matching people"
					renderInput={( params ) => (
						<TextField
							{...params}
							autoFocus
							label={currentName ? 'Reassign to' : 'Assign to'}
							placeholder="Search people"
						/>
					)}
					renderOption={( props, option ) => (
						<Box component="li" {...props} key={option.id}>
							<Box>
								<Typography variant="body1">{option.name || option.email}</Typography>
								{option.name && option.email && (
									<Typography variant="body2" color="text.secondary">{option.email}</Typography>
								)}
							</Box>
						</Box>
					)}
				/>
			</Popover>
		</>
	)
}
